import React, { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Grid,
    InputAdornment,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import moment from "moment";
import { useFetchAllLaboratoriesByPatientQuery } from "scenes/patient/laboratory/LaboratoryApi";

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
    },
    headerBar: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "20px",
        flexWrap: "wrap",
        gap: "12px",
    },
    sectionTitle: {
        fontStyle: "normal",
        fontWeight: 600,
        fontSize: "16px",
        color: "#245B67",
    },
    searchField: {
        minWidth: "260px",
        background: "#FFFFFF",
        borderRadius: "8px",
    },
    tableWrapper: {
        background: "#F8F8FA",
        border: "1px solid #E2E1E4",
        borderRadius: "15px",
        overflow: "hidden",
    },
    tableContainer: {
        maxHeight: "70vh",
    },
    table: {
        borderCollapse: "separate",
        borderSpacing: 0,
    },
    nameCell: {
        position: "sticky",
        left: 0,
        zIndex: 3,
        background: "#F1F2F4",
        fontWeight: 600,
        color: "#245B67",
        borderRight: "1px solid #E2E1E4",
        whiteSpace: "nowrap",
    },
    nameHeaderCell: {
        position: "sticky",
        left: 0,
        zIndex: 4,
        background: "#245B67",
        color: "#FFFFFF",
        fontWeight: 600,
        borderRight: "1px solid #E2E1E4",
    },
    valueCell: {
        verticalAlign: "top",
        minWidth: "130px",
        color: "#2A2A2A",
    },
    valueLine: {
        fontSize: "14px",
        lineHeight: "20px",
    },
    tagChip: {
        fontSize: "11px",
        color: "#5C7A82",
        marginLeft: "4px",
    },
    emptyCell: {
        color: "#B5B5B5",
    },
    summaryStrip: {
        display: "flex",
        gap: "20px",
        padding: "14px 20px",
        background: "#FFFFFF",
        borderBottom: "1px solid #E2E1E4",
        flexWrap: "wrap",
    },
    summaryItem: {
        display: "flex",
        flexDirection: "column",
    },
    summaryLabel: {
        fontSize: "11px",
        textTransform: "uppercase",
        letterSpacing: "0.4px",
        color: "#7A8B91",
    },
    summaryValue: {
        fontSize: "18px",
        fontWeight: 600,
        color: "#245B67",
    },
    noDataMessage: {
        padding: "60px 20px",
        textAlign: "center",
        color: theme.palette.text.disabled,
        fontSize: "1.1em",
    },
}));

const StyledHeaderCell = withStyles({
    root: {
        background: "#245B67",
        color: "#FFFFFF",
        fontWeight: 600,
        whiteSpace: "nowrap",
        borderBottom: "none",
    },
})(TableCell);

const formatUnit = (unit) => {
    if (typeof unit !== "string" || unit.length === 0) return "";
    return unit.replace("percent", "%").replace("per", "/");
};

const formatDateLabel = (recordDate) => {
    const parsed = moment(recordDate, moment.ISO_8601, true);
    if (parsed.isValid()) {
        return parsed.format("DD MMM YYYY");
    }
    const looseParsed = moment(recordDate);
    return looseParsed.isValid() ? looseParsed.format("DD MMM YYYY") : recordDate;
};

const LaboratoryTable = () => {
    const classes = useStyles();
    const { patientId } = useParams();
    const { t } = useTranslation();
    const lang = useCallback((name) => t(`laboratory.table.${name}`), [t]);
    const [nameFilter, setNameFilter] = useState("");

    const { data: laboratoryEntries, isSuccess } = useFetchAllLaboratoriesByPatientQuery(
        { patientId },
        { skip: !patientId },
    );

    const safeEntries = useMemo(() => {
        if (!Array.isArray(laboratoryEntries)) return [];
        return laboratoryEntries.filter(
            (laboratoryEntry) =>
                laboratoryEntry &&
                typeof laboratoryEntry.name === "string" &&
                laboratoryEntry.name.length > 0 &&
                typeof laboratoryEntry.recordDate === "string" &&
                laboratoryEntry.recordDate.length > 0,
        );
    }, [laboratoryEntries]);

    const sortedRecordDates = useMemo(() => {
        const distinctRecordDateSet = new Set(safeEntries.map((laboratoryEntry) => laboratoryEntry.recordDate));
        return Array.from(distinctRecordDateSet).sort((firstRecordDate, secondRecordDate) => {
            const firstMoment = moment(firstRecordDate);
            const secondMoment = moment(secondRecordDate);
            if (!firstMoment.isValid() && !secondMoment.isValid()) return 0;
            if (!firstMoment.isValid()) return 1;
            if (!secondMoment.isValid()) return -1;
            return secondMoment.valueOf() - firstMoment.valueOf();
        });
    }, [safeEntries]);

    const sortedLaboratoryNames = useMemo(() => {
        const distinctLaboratoryNameSet = new Set(safeEntries.map((laboratoryEntry) => laboratoryEntry.name));
        return Array.from(distinctLaboratoryNameSet).sort((firstName, secondName) =>
            firstName.localeCompare(secondName, undefined, { sensitivity: "base" }),
        );
    }, [safeEntries]);

    const laboratoryNameToRecordDateToEntries = useMemo(() => {
        const nameToDateMap = new Map();
        safeEntries.forEach((laboratoryEntry) => {
            const dateMap = nameToDateMap.get(laboratoryEntry.name) || new Map();
            const cellEntries = dateMap.get(laboratoryEntry.recordDate) || [];
            cellEntries.push(laboratoryEntry);
            dateMap.set(laboratoryEntry.recordDate, cellEntries);
            nameToDateMap.set(laboratoryEntry.name, dateMap);
        });
        return nameToDateMap;
    }, [safeEntries]);

    const filteredLaboratoryNames = useMemo(() => {
        const trimmedFilter = nameFilter.trim().toLowerCase();
        if (trimmedFilter.length === 0) return sortedLaboratoryNames;
        return sortedLaboratoryNames.filter((laboratoryName) =>
            laboratoryName.toLowerCase().includes(trimmedFilter),
        );
    }, [sortedLaboratoryNames, nameFilter]);

    const totalEntryCount = safeEntries.length;
    const distinctNameCount = sortedLaboratoryNames.length;
    const distinctDateCount = sortedRecordDates.length;
    const latestRecordDateLabel = sortedRecordDates.length > 0 ? formatDateLabel(sortedRecordDates[0]) : "—";

    if (isSuccess && totalEntryCount === 0) {
        return (
            <div className={classes.tableWrapper}>
                <Typography className={classes.noDataMessage}>{lang("no-data")}</Typography>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            <div className={classes.headerBar}>
                <Typography className={classes.sectionTitle}>{lang("title")}</Typography>
                <TextField
                    className={classes.searchField}
                    variant="outlined"
                    size="small"
                    placeholder={lang("search-placeholder")}
                    value={nameFilter}
                    onChange={(searchEvent) => setNameFilter(searchEvent.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon style={{ color: "#7A8B91" }} />
                            </InputAdornment>
                        ),
                    }}
                />
            </div>

            <div className={classes.tableWrapper}>
                <div className={classes.summaryStrip}>
                    <div className={classes.summaryItem}>
                        <span className={classes.summaryLabel}>{lang("summary-entries")}</span>
                        <span className={classes.summaryValue}>{totalEntryCount}</span>
                    </div>
                    <div className={classes.summaryItem}>
                        <span className={classes.summaryLabel}>{lang("summary-distinct-names")}</span>
                        <span className={classes.summaryValue}>{distinctNameCount}</span>
                    </div>
                    <div className={classes.summaryItem}>
                        <span className={classes.summaryLabel}>{lang("summary-record-dates")}</span>
                        <span className={classes.summaryValue}>{distinctDateCount}</span>
                    </div>
                    <div className={classes.summaryItem}>
                        <span className={classes.summaryLabel}>{lang("summary-latest")}</span>
                        <span className={classes.summaryValue}>{latestRecordDateLabel}</span>
                    </div>
                </div>

                <TableContainer
                    component={Paper}
                    elevation={0}
                    className={classes.tableContainer}
                    style={{ background: "transparent" }}
                >
                    <Table stickyHeader className={classes.table} size="small">
                        <TableHead>
                            <TableRow>
                                <StyledHeaderCell className={classes.nameHeaderCell}>
                                    {lang("name-column")}
                                </StyledHeaderCell>
                                {sortedRecordDates.map((recordDate) => (
                                    <StyledHeaderCell key={recordDate} align="center">
                                        {formatDateLabel(recordDate)}
                                    </StyledHeaderCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredLaboratoryNames.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={sortedRecordDates.length + 1}
                                        className={classes.noDataMessage}
                                    >
                                        {lang("no-match")}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredLaboratoryNames.map((laboratoryName) => {
                                    const dateMap = laboratoryNameToRecordDateToEntries.get(laboratoryName);
                                    return (
                                        <TableRow key={laboratoryName} hover>
                                            <TableCell className={classes.nameCell}>{laboratoryName}</TableCell>
                                            {sortedRecordDates.map((recordDate) => {
                                                const cellEntries = dateMap?.get(recordDate) || [];
                                                if (cellEntries.length === 0) {
                                                    return (
                                                        <TableCell
                                                            key={recordDate}
                                                            align="center"
                                                            className={`${classes.valueCell} ${classes.emptyCell}`}
                                                        >
                                                            —
                                                        </TableCell>
                                                    );
                                                }
                                                return (
                                                    <TableCell
                                                        key={recordDate}
                                                        align="center"
                                                        className={classes.valueCell}
                                                    >
                                                        {cellEntries.map((laboratoryEntry, entryIndex) => {
                                                            const labValueText =
                                                                laboratoryEntry.labValue !== null &&
                                                                laboratoryEntry.labValue !== undefined &&
                                                                laboratoryEntry.labValue !== ""
                                                                    ? String(laboratoryEntry.labValue)
                                                                    : "—";
                                                            const unitText = formatUnit(laboratoryEntry.unit);
                                                            const tagText =
                                                                typeof laboratoryEntry.tag === "string" &&
                                                                laboratoryEntry.tag.length > 0
                                                                    ? `(${laboratoryEntry.tag})`
                                                                    : "";
                                                            return (
                                                                <div
                                                                    key={`${laboratoryEntry.id || entryIndex}`}
                                                                    className={classes.valueLine}
                                                                >
                                                                    <span>
                                                                        {labValueText}
                                                                        {unitText.length > 0 ? ` ${unitText}` : ""}
                                                                    </span>
                                                                    {tagText.length > 0 && (
                                                                        <span className={classes.tagChip}>
                                                                            {tagText}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
            <Grid />
        </div>
    );
};

export default LaboratoryTable;
